import React from 'react';
import { useTranslation } from 'react-i18next';

import { SearchResultsHeadless, SearchResultsList } from '@uncinc/react-searchkit';

import type {
  SearchAllContentAnnualReportHit,
  SearchAllContentArticleHit,
  SearchAllContentDepartmentHit,
  SearchAllContentEquipmentHit,
  SearchAllContentExpertiseHit,
  SearchAllContentHit,
  SearchAllContentPartnerHit,
  SearchAllContentProfileHit,
  SearchAllContentProjectHit,
  SearchAllContentThemeHit,
  SearchAllContentVacancyHit,
} from '../../../SearchAllContent/types';

import SearchAllContentPagination from '../../../SearchAllContent/Pagination';
import SearchAllContentSearchBar from '../../../SearchAllContent/SearchBar';
import MetaSearchEmptyState from '../EmptyState';
import MetaSearchFacets from '../Facets';
import { sortByCustomOrder } from '../metaParagraphsHelpers';
import type { MetaSearchProps } from '..';

import ArticleHit from '../Results/ArticleHit';
import ProjectHit from '../Results/ProjectHit';
import ProfileHit from '../Results/ProfileHit';
import DepartmentHit from '../Results/DepartmentHit';
import ThemeHit from '../Results/ThemeHit';
import EquipmentHit from '../Results/EquipmentHit';
import ExpertiseHit from '../Results/ExpertiseHit';
import VacancyHit from '../Results/VacancyHit';
import PartnerHit from '../Results/PartnerHit';
import AnnualReportHit from '../Results/AnnualReportHit';

const RenderItem = (hit: SearchAllContentHit) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { content_type, profile_type, vid } = hit;

  let type;
  if (content_type?.[0]) {
    [type] = content_type;
  } else if (profile_type?.[0]) {
    [type] = profile_type;
  } else if (vid?.[0]) {
    [type] = vid;
  }

  switch (type) {
    case 'article': {
      return <ArticleHit hit={hit as SearchAllContentArticleHit} />;
    }
    case 'project': {
      return <ProjectHit hit={hit as SearchAllContentProjectHit} />;
    }
    case 'default_profile': {
      return <ProfileHit hit={hit as SearchAllContentProfileHit} />;
    }
    case 'departments': {
      return <DepartmentHit hit={hit as SearchAllContentDepartmentHit} />;
    }
    case 'themes': {
      return <ThemeHit hit={hit as SearchAllContentThemeHit} />;
    }
    case 'equipment': {
      return <EquipmentHit hit={hit as SearchAllContentEquipmentHit} />;
    }
    case 'expertises': {
      return <ExpertiseHit hit={hit as SearchAllContentExpertiseHit} />;
    }
    case 'vacancy': {
      return <VacancyHit hit={hit as SearchAllContentVacancyHit} />;
    }
    case 'partner': {
      return <PartnerHit hit={hit as SearchAllContentPartnerHit} />;
    }
    case 'annual_report': {
      return <AnnualReportHit hit={hit as SearchAllContentAnnualReportHit} />;
    }
    default: {
      return <div />;
    }
  }
};

export type MetaSearchInnerProps = Pick<MetaSearchProps, 'sortingList' | 'withSearchbar' | 'withFacets' | 'withPagination'>;

const MetaSearchInner: React.FC<MetaSearchInnerProps> = (props) => {
  const {
    sortingList = undefined,
    withSearchbar = false,
    withFacets = false,
    withPagination = false,
  } = props;

  const { t } = useTranslation();

  return (
    <div className="MetaSearch">
      {withSearchbar && (
        <SearchAllContentSearchBar
          className="MetaSearch__SearchBar"
          title={t('searchkit.search_bar.title')}
        />
      )}

      {withFacets && (
        <MetaSearchFacets />
      )}

      <div className="ParagraphMeta__results">
        <SearchResultsHeadless>
          {(hits) => {
            const sortedItems = sortingList ? sortByCustomOrder(hits, sortingList) : hits;

            return (
              <SearchResultsList
                renderItem={RenderItem}
                hits={sortedItems}
                emptyState={<MetaSearchEmptyState />}
              />
            );
          }}
        </SearchResultsHeadless>
      </div>

      {withPagination && (
        <SearchAllContentPagination className="MetaSearch__Pagination" />
      )}
    </div>
  );
};

export default MetaSearchInner;
